

import { useRouter } from 'next/router';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { addDays, parse, format, getDay, differenceInCalendarDays } from 'date-fns';
import { Formik, Form, Field, FieldProps } from 'formik';
import styles from '../styles/Home.module.css';
import { getStrapiURL, api } from '@/api/api';
import Button from '@/components/Button';
import DateField from '@/components/DateField';
import Flex from '@/components/Flex';
import FormGroup from '@/components/FormGroup';
import SelectField from '@/components/SelectField';
import {
  schema,
  FormFieldsType,
  timeOptions,
} from '@/forms/itinerary';
import { minPickupDate } from '@/helpers/itinerary';
import { BusinessHourType, DepotType } from '@/types';
import { useReservationBar } from '@/contexts/reservationBar';
import Modal from 'components/Modal';
import { brFormatToUsaFormat, kayakFormatToUsaFormat, kayakTimeToNormalTime } from '@/helpers/fdate';
import Image from 'next/image';
import { useSettings } from '@/contexts/settings';
import Link from 'next/link';
import { useLayout } from '@/contexts/layout';
import { getDepotsData } from '@/utils/depotUtils';
import ModalIndisponibilidade from '@/components/Modals/Modal-Indisponibilidade/ModalIndisponibilidade';
import { Cars } from '@/types/coral-types';
import { getCdpIdFromQuery } from '@/helpers/query';
import { sendViewItemEvent } from '@/utils/dataLayer/view_item_list';
import { useSession } from '@/contexts/session';
interface HomeProps {
  depotsOptions: DepotOptionType[];
  serverErrors: any[];
}

const modalDefault = {
  isOpen: false,
  title: 'Alerta',
  message: '',
}

const MENSAL_FLEX_MIN_DAYS = 28;
const MENSAL_FLEX_FRANCHISE = '1000';
const MENSAL_FLEX_MODAL_PARAM = 'rac_long_rental';
const MENSAL_FLEX_URL = process.env.NEXT_PUBLIC_MF_URL || 'https://mensalflex.aluguefoco.com.br';
const MENSAL_FLEX_VEHICLES_URL = `${MENSAL_FLEX_URL.replace(/\/$/, '')}/veiculos`;

const dayMap: { [key: number]: string } = {
  0: 'Domingo',
  1: 'Segunda-feira',
  2: 'Terça-feira',
  3: 'Quarta-feira',
  4: 'Quinta-feira',
  5: 'Sexta-feira',
  6: 'Sábado',
};

const getFilteredTimeOptions = (
  depotValue: string | undefined,
  selectedDate: Date | undefined,
  depots: DepotOptionType[],
  allTimeOptions: typeof timeOptions,
): typeof timeOptions => {
  if (!depotValue || !selectedDate) {
    return allTimeOptions;
  }

  const selectedDepot = depots.find(d => d.value === depotValue);

  if (!selectedDepot || !selectedDepot.business_hours || selectedDepot.business_hours.length === 0) {
    return allTimeOptions;
  }

  const dayOfWeekJs = getDay(selectedDate);
  const dayOfWeekString = dayMap[dayOfWeekJs];

  const businessHourForDay = selectedDepot.business_hours.find(bh => bh.day === dayOfWeekString);

  if (!businessHourForDay) {
    return allTimeOptions;
  }

  if (businessHourForDay.is_24hours === true || (businessHourForDay.open === null && businessHourForDay.close === null)) {
    return allTimeOptions;
  }

  if (businessHourForDay.open && businessHourForDay.close) {
    const openTime = businessHourForDay.open.substring(0, 5);
    const closeTime = businessHourForDay.close.substring(0, 5);

    return allTimeOptions.filter(option => {
      return option.value >= openTime && option.value <= closeTime;
    });
  }
  return allTimeOptions;
};


export default function Home({ depotsOptions, serverErrors }: HomeProps) {
  const router = useRouter();
  const { setValue, updateBar } = useReservationBar();
  const [isLoading, setIsLoading] = useState(false);
  const [modal, setModal] = useState(modalDefault);
  const {
    vantagens,
    show_secondary_banners,
    secondary_banners_start_date,
    secondary_banners_end_date,
    secondary_banner_desktop,
    secondary_banner_mobile,
  } = useSettings();
  const [isReturnStoreEdited, setIsReturnStoreEdited] = useState(false);
  const isSameStore = useMemo(() => router.query?.pickup_store !== undefined && router.query?.return_store !== undefined && router.query?.pickup_store === router.query?.return_store && !isReturnStoreEdited, [router.query, isReturnStoreEdited]);
  const { downloadApp, setDownloadApp, setIsPromotionBannerVisible } = useLayout();
  const [isUnavailabilityModalOpen, setIsUnavailabilityModalOpen] = useState(false);

  const [pickupTimeOptions, setPickupTimeOptions] = useState(timeOptions);
  const [returnTimeOptions, setReturnTimeOptions] = useState(timeOptions);
  const dataLayerEventSent = useRef(false);
  const { customer } = useSession();
  const login = !!customer;

  const shouldShowSecondaryBanners = useMemo(() => {
    if (
      !show_secondary_banners ||
      !secondary_banners_start_date ||
      !secondary_banners_end_date
    ) {
      return false;
    }

    const now = new Date();
    const startDate = new Date(secondary_banners_start_date);
    const endDate = new Date(secondary_banners_end_date);

    return now >= startDate && now <= endDate;
  }, [
    show_secondary_banners,
    secondary_banners_start_date,
    secondary_banners_end_date,
  ]);

  const desktopBanner = shouldShowSecondaryBanners && secondary_banner_desktop
    ? secondary_banner_desktop
    : vantagens.image_suaViagem_desktop;

  const mobileBanner = shouldShowSecondaryBanners && secondary_banner_mobile
    ? secondary_banner_mobile
    : vantagens.image_suaViagem_mobile;

  const clearReservationFromLocalStorage = () => {
    if (typeof window !== 'undefined') {
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('FOCO-Reserva-') || key === 'condutor-form') {
          localStorage.removeItem(key);
        }
      });
    }
  };

  useEffect(() => {
    clearReservationFromLocalStorage();
    setDownloadApp(true);
    setIsPromotionBannerVisible(true);
    updateBar({
      title: 'Itinerário',
      step: 1,
      progress: 0,
    });
    return () => {
      setDownloadApp(false);
      setIsPromotionBannerVisible(false);
    }
  }, []);

  useEffect(() => {
    if (typeof router.query.uiCode !== 'undefined') {
      const uiCode = router.query.uiCode;

      if (uiCode === 'NO_VEHICLES_AVAILABLE' && !dataLayerEventSent.current) {
        dataLayerEventSent.current = true;

        const handleNoVehiclesAvailable = async () => {
          const {
            pickup_store,
            return_store,
            pickup_date,
            return_date,
            pickup_time,
            return_time,
            coupon,
            rate_id_temp
          } = router.query;

          const depot = depotsOptions.find(d => d.value === pickup_store);
          if (!depot || !rate_id_temp) return;

          const cdp_id = getCdpIdFromQuery(router.query);
          const vehicleData = {
            pickup_datetime: `${pickup_date}T${pickup_time}:00`,
            return_datetime: `${return_date}T${return_time}:00`,
            pickup_store: pickup_store,
            return_store: return_store,
            promo_code: coupon as string || undefined,
            cdp_id,
          };

          try {
            const { data: vehicles } = await api.post<Cars>(
              `/rac/vehicles?depot.id=${depot.id}&rate_id=${rate_id_temp}`,
              vehicleData,
            );
            sendViewItemEvent(depot, router.query, vehicles, login);
          } catch (error) {
            console.error("Failed to fetch vehicles for dataLayer event:", error);
            sendViewItemEvent(depot, router.query, [], login);
          }
        };

        handleNoVehiclesAvailable();
      }

      const newQuery = { ...router.query };
      delete newQuery.uiCode;
      delete newQuery.rate_id_temp;
      router.replace({ pathname: '/', query: newQuery }, undefined, { shallow: true });

      switch (uiCode) {
        case '2724':
          setModal({
            isOpen: true,
            title: 'Aviso',
            message: 'Não é possível drop off entre essas duas lojas'
          });
          break;
        case '1655':
          setModal({
            isOpen: true,
            title: 'Aviso',
            message: 'Esta loja não está aberta no horário selecionado para Retirada. Por gentileza, escolha outro horário.'
          });
          break;
        case '1656':
          setModal({
            isOpen: true,
            title: 'Aviso',
            message: 'Esta loja não está aberta no horário selecionado para Devolução. Por gentileza, escolha outro horário.'
          });
          break;
        case '1657':
          setModal({
            isOpen: true,
            title: 'Aviso',
            message: 'Esta loja não está aberta no horário selecionado para Retirada. Por gentileza, escolha outro horário.'
          });
          break;
        case '1658':
          setModal({
            isOpen: true,
            title: 'Aviso',
            message: 'Esta loja não está aberta no horário selecionado para Devolução. Por gentileza, escolha outro horário.'
          });
          break;
        case 'DT30':
          setModal({
            isOpen: true,
            title: 'Aviso',
            message: 'O horário escolhido está muito próximo. Escolha um horário com no mínimo 30 minutos de antecedência'
          });
          break;
        case 'NO_VEHICLES_AVAILABLE':
          setIsUnavailabilityModalOpen(true);
          break;
        default:
          setModal({
            isOpen: true,
            title: 'Erro',
            message: 'Houve um erro, favor tentar novamente'
          });
      }
    }
  }, [router.query, depotsOptions, router]);

  const buildMensalFlexRedirectUrl = (query: Record<string, string | number>) => {
    const url = new URL(MENSAL_FLEX_VEHICLES_URL, typeof window !== 'undefined' ? window.location.origin : undefined);

    Object.entries({
      pickup_date: query.pickup_date,
      pickup_time: query.pickup_time,
      return_date: query.return_date,
      return_time: query.return_time,
      franchise: MENSAL_FLEX_FRANCHISE,
      pickup_store: query.pickup_store,
      return_store: query.return_store,
      driver: query.driver,
      coupon: query.coupon,
      [MENSAL_FLEX_MODAL_PARAM]: '1',
    }).forEach(([key, value]) => {
      url.searchParams.set(key, String(value));
    });

    return url.toString();
  };

  const onSubmit = (values: FormFieldsType) => {
    const returnStore = values.return_store === 'SAME' ? values.pickup_store : values.return_store;
    const depotOption = depotsOptions.find(
      (depot: any) => depot.value === values.pickup_store,
    );
    let query: any = {
      pickup_store: values.pickup_store,
      pickup_date: format(values.pickup_date, 'yyyy-MM-dd'),
      pickup_time: values.pickup_time,
      return_store: returnStore,
      return_date: format(values.return_date, 'yyyy-MM-dd'),
      return_time: values.return_time,
      driver: router.query.driver || 21,
      coupon: values.promo_code || '',
    };
    if (router.query.cdp_id) {
      query = {
        ...query,
        cdp_id: router.query.cdp_id,
      }
    }

    const rentalDays = differenceInCalendarDays(values.return_date, values.pickup_date);

    if (rentalDays >= MENSAL_FLEX_MIN_DAYS) {
      setIsLoading(true);
      window.location.href = buildMensalFlexRedirectUrl(query);
      return;
    }

    setIsLoading(true);
    setValue(1, depotOption?.label || values.pickup_store);
    router.push({
      pathname: '/veiculos',
      query: {
        ...query,
        cdp_id: query.cdp_id || "FOCPRE"
      }
    })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getInitialValue = (key: string, initial: any): any => {
    if (typeof router.query[key] === 'string') {
      const value = decodeURIComponent(router.query[key] as string);
      if (key === 'pickup_date' || key === 'return_date') {
        const formats = ['dd/MM/yyyy', 'dd/MM/yy'];
        let parseFormat = 'yyyy-MM-dd';
        if (formats.includes(router.query.date_format as string)) {
          parseFormat = router.query.date_format as string;
        }
        const parsed = parse(value, parseFormat, new Date());
        parsed.setHours(12);
        parsed.setMinutes(0);
        return parsed.toString() === 'Invalid Date' ? initial : parsed;
      }
      if (key === 'pickup_time' || key === 'return_time') {
        const [hour, time] = (router.query[key] as string)?.split(':');
        const auxDate = new Date();
        auxDate.setHours(hour as any);
        auxDate.setMinutes(time as any);
        return format(auxDate, 'HH:mm');
      }
      return value;
    }
    return initial;
  }

  const closeModal = () => {
    setModal(modalDefault);
  }

  const confirmModal = () => {
    closeModal();
  }

  const applyCoupon = (coupon: string) => {
    router.push({
      pathname: '/',
      query: {
        ...router.query,
        coupon: coupon,
      }
    });
  }

  function getMobileAppLink(): string {
    if (typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent || navigator.vendor || '';
      if (/android/i.test(userAgent)) {
        return 'https://play.google.com/store/apps/details?id=foco.APP';
      }
      if (/iPad|iPhone|iPod/.test(userAgent) && !(window as any).MSStream) {
        return 'https://apps.apple.com/br/app/foco-aluguel-de-carros/id1622202895';
      }
    }
    return 'https://play.google.com/store/apps/details?id=foco.APP';
  }

  const handleAppRedirect = useCallback(() => {
    const deepLink = 'focoapp://home';
    const storeUrl = getMobileAppLink();
    let timeout: NodeJS.Timeout;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(timeout);
        window.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange, false);

    timeout = setTimeout(() => {
      window.location.href = storeUrl;
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    }, 1000);

    window.location.href = deepLink;
  }, []);

  return (
    <div className={styles.main__container}>
      {downloadApp &&
        <div className={styles.download__app}>
          <p className={styles.download__app__text}>Baixe o <strong>aplicativo da Foco</strong> e tenha mais praticidade</p>
          <Link href="/abrir-app" legacyBehavior>
            <a
              className={styles.download__app__link}
              target="_blank"
              rel="noopener noreferrer"
            >
              Baixar agora
            </a>
          </Link>
        </div>
      }
      <h2 className={styles.main__title}>Sua Viagem</h2>
      <hr className={styles.hr} />
      <Modal
        isOpen={modal.isOpen}
        title={modal.title}
        onClose={() => closeModal()}
        onConfirm={() => confirmModal()}
      >
        {modal.message}
      </Modal>
      <ModalIndisponibilidade
        isOpen={isUnavailabilityModalOpen}
        onClose={() => setIsUnavailabilityModalOpen(false)}
      />
      <div className={styles.wrapp__container}>
        <div className={styles.form__wrap}>
          <Formik
            initialValues={{
              pickup_store: getInitialValue('pickup_store', ''),
              pickup_date: getInitialValue('pickup_date', addDays(new Date(), 1)),
              pickup_time: getInitialValue('pickup_time', '12:00'),
              return_store: getInitialValue('return_store', 'SAME'),
              return_date: getInitialValue('return_date', addDays(new Date(), 2)),
              return_time: getInitialValue('return_time', '12:00'),
              promo_code: getInitialValue('coupon', ""),
            }}
            onSubmit={onSubmit}
            validationSchema={schema}
          >
            {({ errors, touched, setFieldValue, values, setTouched }) => {
              const { couponToApply, setCouponToApply } = useLayout();

              useEffect(() => {
                if (couponToApply) {
                  setFieldValue('promo_code', couponToApply.toUpperCase());
                  setCouponToApply(null);
                }
              }, [couponToApply, setFieldValue, setCouponToApply]);

              useEffect(() => {
                const newOptions = getFilteredTimeOptions(values.pickup_store, values.pickup_date, depotsOptions, timeOptions);
                setPickupTimeOptions(newOptions);
                if (values.pickup_time && !newOptions.find(opt => opt.value === values.pickup_time)) {
                  setFieldValue('pickup_time', newOptions.length > 0 ? newOptions[0].value : '');
                } else if (!values.pickup_time && newOptions.length > 0) {
                  setFieldValue('pickup_time', newOptions[0].value);
                }
              }, [values.pickup_store, values.pickup_date, depotsOptions, setFieldValue, values.pickup_time]);

              useEffect(() => {
                const actualReturnStore = values.return_store === 'SAME' ? values.pickup_store : values.return_store;
                const newOptions = getFilteredTimeOptions(actualReturnStore, values.return_date, depotsOptions, timeOptions);
                setReturnTimeOptions(newOptions);
                if (values.return_time && !newOptions.find(opt => opt.value === values.return_time)) {
                  setFieldValue('return_time', newOptions.length > 0 ? newOptions[0].value : '');
                } else if (!values.return_time && newOptions.length > 0) {
                  setFieldValue('return_time', newOptions[0].value);
                }
              }, [values.return_store, values.pickup_store, values.return_date, depotsOptions, setFieldValue, values.return_time]);

              return (
                <Form>
                  <div>
                    <div className={styles.form__inputs}>
                      <Flex direction="column" flex={1}>
                        <FormGroup
                          label="Local de retirada"
                          htmlFor="pickup_store"
                          errors={errors?.pickup_store}
                          touched={touched?.pickup_store}
                        >
                          <Field
                            className={errors?.pickup_store && touched?.pickup_store ? 'field__has_error' : ''}
                            name="pickup_store"
                            id="pickup_store"
                            component={SelectField}
                            options={depotsOptions}
                            placeholder="Selecionar"
                            afterChange={(option: any) => {
                              if (isSameStore) {
                                setFieldValue('return_store', option.value);
                              }
                            }}
                          />
                        </FormGroup>
                        <Flex direction="row" gap={'1rem'}>
                          <FormGroup
                            label="Data da Retirada"
                            htmlFor="pickup_date"
                            errors={errors?.pickup_date as string}
                            touched={true}
                          >
                            <Field
                              className={errors?.pickup_date ? 'field__has_error' : ''}
                              name="pickup_date"
                              id="pickup_date"
                              component={DateField}
                              minDate={minPickupDate}
                              onChange={(val: Date) => {
                                if (!touched.return_date || val > values.return_date) {
                                  setFieldValue('return_date', addDays(val, 1));
                                }
                              }}
                              withPortal
                            />
                          </FormGroup>
                          <FormGroup
                            label="Hora da Retirada"
                            htmlFor="pickup_time"
                            errors={errors?.pickup_time}
                            touched={true}
                          >
                            <Field
                              className={errors?.pickup_time && touched?.pickup_time ? 'field__has_error' : ''}
                              name="pickup_time"
                              id="pickup_time"
                              component={SelectField}
                              options={pickupTimeOptions}
                            />
                          </FormGroup>
                        </Flex>
                        <Flex direction="column" flex={1}>
                          <FormGroup
                            label="Local de Devolução"
                            htmlFor="return_store"
                            errors={errors?.return_store}
                            touched={touched?.return_store}
                          >
                            <Field
                              className={errors?.return_store && touched?.return_store ? 'field__has_error' : ''}
                              name="return_store"
                              id="return_store"
                              component={SelectField}
                              options={[
                                {
                                  label: 'Mesmo local de retirada',
                                  value: 'SAME',
                                },
                                ...depotsOptions,
                              ]}
                              afterChange={() => {
                                setIsReturnStoreEdited(true);
                              }}
                            />
                          </FormGroup>
                          <Flex direction="row" gap={'1rem'}>
                            <FormGroup
                              label="Data da Devolução"
                              htmlFor="return_date"
                              errors={errors?.return_date as string}
                              touched={true}
                            >
                              <Field name="return_date" id="return_date">
                                {({ field, form, meta }: FieldProps) => (
                                  <DateField
                                    form={form}
                                    meta={meta}
                                    minDate={values.pickup_date}
                                    withPortal
                                    field={field}
                                    type="date"
                                    onBlur={() => setTouched({ return_date: true })}
                                  />
                                )}
                              </Field>
                            </FormGroup>
                            <FormGroup
                              label="Hora da Devolução"
                              htmlFor="return_time"
                              errors={errors?.return_time}
                              touched={true}
                            >
                              <Field
                                className={errors?.return_time && touched?.return_time ? 'field__has_error' : ''}
                                name="return_time"
                                id="return_time"
                                component={SelectField}
                                options={returnTimeOptions}
                              />
                            </FormGroup>
                          </Flex>
                          <FormGroup
                            label="Cupom de Desconto"
                            htmlFor="promo_code"
                            errors={errors?.promo_code}
                            touched={true}
                          >
                            <div className={styles.cupom__wrapp}>
                              <Field
                                className={`cupom__field text__field ${errors?.promo_code && touched?.promo_code ? 'field__has_error' : ''}`}
                                name="promo_code"
                                id="promo_code"
                                maxLength={10}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                  const { value } = event.target;
                                  setFieldValue('promo_code', value.toUpperCase());
                                  if (value.length === 6) {
                                  }
                                }}
                              />
                            </div>
                          </FormGroup>
                        </Flex>
                      </Flex>
                    </div>
                    <hr className={styles.hr} />
                    <FormGroup className={styles.submit_wrap} isBtnSubmit>
                      <Button
                        id="submitBtn"
                        className={styles.next__button}
                        type="submit"
                        hasRightIcon
                        isLoading={isLoading}
                      >
                        {isLoading ? (
                          'Carregando ...'
                        ) : (
                          <>
                            Continuar
                          </>
                        )}
                      </Button>
                    </FormGroup>
                  </div>
                </Form>
              )
            }}
          </Formik>
        </div>
        <div className={styles.image__container}>
          {desktopBanner &&
            <Image
              src={getStrapiURL(desktopBanner.url)}
              alt='foto'
              width={340}
              height={500}
              className={styles.home__image}
            />
          }
        </div>
        <div className={styles.image__container__mob}>
          {mobileBanner &&
            <Image
              src={getStrapiURL(mobileBanner.url)}
              alt='foto'
              width={355}
              height={146}
              className={styles.home__image}
            />
          }
        </div>
      </div>
    </div>
  );
}

export interface DepotOptionType {
  id: string | number;
  label: string;
  value: string;
  business_hours?: BusinessHourType[];
}

export async function getServerSideProps({ query }: any) {
  const depotsOptions: DepotOptionType[] = [];
  const serverErrors: any[] = [];
  if (query.origin === 'SKYSCANNER' && typeof query.deeplink === 'undefined') {
    const { pickup_location, pickup_date, return_location, return_date, ...rest } = query;
    const params = new URLSearchParams({
      pickup_store: pickup_location,
      pickup_date: brFormatToUsaFormat(pickup_date),
      return_store: return_location,
      return_date: brFormatToUsaFormat(return_date),
      ...rest,
      driver: query.driver || 21,
      coupon: query.coupon || '',
      deeplink: '1',
    });
    return {
      redirect: {
        permanent: false,
        destination: '/veiculos?' + params.toString(),
      }
    }
  } else if (query.utm_source === 'kayak' && typeof query.deeplink === 'undefined') {
    const { PICKUP_LOC, PICKUP_DATE, PICKUP_MONTH_YEAR, PICKUP_TIME, DROPOFF_LOC, DROPOFF_DATE, DROPOFF_MONTH_YEAR, DROPOFF_TIME, ...rest } = query;
    const params = new URLSearchParams({
      pickup_store: PICKUP_LOC,
      pickup_date: kayakFormatToUsaFormat(PICKUP_DATE, PICKUP_MONTH_YEAR),
      pickup_time: kayakTimeToNormalTime(PICKUP_TIME),
      return_store: DROPOFF_LOC,
      return_date: kayakFormatToUsaFormat(DROPOFF_DATE, DROPOFF_MONTH_YEAR),
      return_time: kayakTimeToNormalTime(DROPOFF_TIME),
      cdp_id: 'KAYAK',
      ...rest,
      driver: query.driver || 21,
      coupon: query.coupon || '',
      origin: 'KAYAK',
      deeplink: '1',
    });
    return {
      redirect: {
        permanent: false,
        destination: '/veiculos?' + params.toString(),
      }
    }
  }
  try {

    const data = await getDepotsData();

    data.sort((a: DepotType, b: DepotType) => {
      if (a.title < b.title) return -1
      if (a.title > b.title) return 1
      return 0
    }).map((depot: DepotType) => {
      depotsOptions.push({
        id: depot.id,
        label: depot.title,
        value: depot.coral_code as string,
        business_hours: depot.business_hours
      });
    });
    return {
      props: {
        depotsOptions,
        serverErrors,
      },
    };
  } catch (err: any) {

    const serverErrors = [
      {
        code: 0,
        message:
          'Houve um erro no servidor, não conseguimos carregar as lojas',
      },
    ];
    return {
      props: {
        depotsOptions,
        serverErrors,
      },
    };
  }
}
